/**
 * The hand-off from the menu shell into a match.
 *
 * Entering the arena is a real page navigation — `/arena.html` is its own document with its own
 * bundle — and until this module existed the player paid for that in full view: the shell
 * vanished on click, the browser painted a blank document, downloaded the arena's bundle cold,
 * and only then did the arena's own loading screen appear and start warming assets. The
 * loading screen was never the problem. Everything *before* it was.
 *
 * So the shell covers the navigation itself. On click it mounts the arena's own boot screen
 * (`mountBootScreen`, same key art, same cyan bar, same labels — a second design would
 * announce the seam this module exists to hide), warms the arena document, the hashed module
 * bundle named inside it and the weapon and character GLBs from `prefetch-manifest.ts`, and
 * only then navigates. The arena's screen picks the bar up where this one left it
 * (`recordBootFloor`), so the fill is continuous across the document swap.
 *
 * Two decisions worth keeping:
 *
 *  - **The wait is capped** (`HANDOFF_CAP_MS`). A dead asset host must never trap a player on
 *    a loading screen. Past the cap the navigation happens with the cache half-warm, which
 *    costs a hitch during warmup and nothing else — the arena re-prefetches everything and
 *    every loader in the renderer already degrades to on-demand loading.
 *  - **The bundle URL is scraped, not hardcoded.** It is content-hashed by Vite, it is the
 *    largest single thing the navigation waits on, and a stale hardcoded name would warm a
 *    404 while the real bundle downloads cold — a bug with no symptom but a slow entry.
 *
 * This matters most on the matchmaking path, where ten accepted clients navigate within a
 * second of each other and hit the same asset host at once. That is the case the arena boot
 * screen's own comment was written for; this is its other half.
 */
import {
  BOOT_FLOOR_CEILING,
  BootProgress,
  mountBootScreen,
  prefetchIntoCache,
  recordBootFloor,
  type BootStage,
} from '../arena/boot-loading';
import { characterModelPrefetchUrls, weaponPrefetchUrls } from '../arena/prefetch-manifest';

/**
 * How long the shell will wait on the network before navigating anyway.
 *
 * 6 seconds: long enough that a normal 4G entry finishes its warm inside it (the weapon set
 * and two character GLBs are a few MB, fetched in parallel), short enough that a stalled fetch
 * does not read as a hung game — a player watching a still bar past about five seconds starts
 * reloading the tab, which is strictly worse than entering warm-ish and hitching once.
 */
export const HANDOFF_CAP_MS = 6_000;

/**
 * The shell's share of the bar.
 *
 * `arena` is deliberately never completed here: it is the 60% of the bar that belongs to the
 * work only the next document can do (physics engine, server handshake, map build), so the
 * shell's screen tops out at 40% and hands over rather than filling and resetting. That
 * ceiling is what `BOOT_FLOOR_CEILING` guards on the other side.
 *
 * The labels are English literals, not `t()` calls: the catalogue has no keys for them yet and
 * `t()` renders a missing key as the key itself, which would put `play.loading.weapons` on
 * screen. Swap them for `t('play.loading.client' | 'weapons' | 'characters' | 'entering')`
 * once those land in `i18n/locales/en_GB.json`.
 */
export const HANDOFF_STAGES: readonly BootStage[] = [
  { id: 'client', label: 'Downloading game', labelKey: 'play.loading.client', weight: 12 },
  { id: 'weapons', label: 'Loading weapons', labelKey: 'play.loading.weapons', weight: 12 },
  { id: 'characters', label: 'Loading characters', labelKey: 'play.loading.characters', weight: 16 },
  { id: 'arena', label: 'Entering match', labelKey: 'play.loading.entering', weight: 60 },
];

export interface EnterArenaOptions {
  /** Overrides the first stage's label — for a path that wants to say something more specific
   * than "Downloading game" (a rejoin, say). Cosmetic only. */
  label?: string;
  /** Where the overlay mounts. Defaults to `document.body`; the screen is `fixed inset-0`. */
  container?: HTMLElement;
  /** Seams, for tests. Production passes none of them. */
  navigate?: (url: string) => void;
  fetchImpl?: typeof fetch;
  capMs?: number;
}

export interface ArenaHandoff {
  /** Resolves when the navigation has been issued (or refused). Callers in the shell ignore
   * it — the document is going away — but a test has to be able to await the decision. */
  readonly settled: Promise<void>;
  /** Tears the overlay down. Called automatically when the navigation is refused. */
  dispose(): void;
}

/**
 * Covers the navigation to `target` with the arena's loading screen and warms its assets.
 *
 * The overlay is mounted **synchronously**, before the first `await`: a click that leaves the
 * shell on screen for even one frame while a fetch is set up reads as an unresponsive button.
 */
export function enterArena(target: string, opts: EnterArenaOptions = {}): ArenaHandoff {
  const container = opts.container ?? document.body;
  const fetchImpl = opts.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const capMs = opts.capMs ?? HANDOFF_CAP_MS;
  const navigate =
    opts.navigate ??
    ((url: string) => {
      window.location.href = url;
    });

  const stages = opts.label
    // `labelKey` is dropped along with the swap: the renderer prefers the key, so leaving it
    // in place would quietly ignore the caller's override.
    ? HANDOFF_STAGES.map((stage) => (stage.id === 'client' ? { ...stage, label: opts.label!, labelKey: undefined } : stage))
    : HANDOFF_STAGES;

  // Floor 0: this machine is the *writer* of the hand-off floor. Defaulting it would consume
  // a floor left by an earlier hand-off and start the shell's own bar part-filled.
  const progress = new BootProgress(stages, 0);
  const screen = mountBootScreen(container, progress);

  let disposed = false;
  const dispose = (): void => {
    disposed = true;
    screen.dispose();
  };

  const settled = (async () => {
    try {
      await Promise.race([warm(target, progress, fetchImpl), delay(capMs)]);
    } catch (error) {
      // `warm` swallows its own failures; anything arriving here is a programming error, and
      // it must still not strand the player on a loading screen.
      console.warn('[handoff] asset warm failed', error);
    }
    if (disposed) return;
    // Hand the bar's position to the next document before leaving this one.
    recordBootFloor(Math.min(BOOT_FLOOR_CEILING, progress.overall()));
    try {
      navigate(target);
    } catch (error) {
      // A blocked or refused navigation (an interceptor, a test stub, a browser that says no).
      // Leaving the overlay up would be a dead screen over a working menu.
      console.warn('[handoff] navigation to the arena was refused', error);
      dispose();
    }
  })();

  return { settled, dispose };
}

/**
 * Everything worth having warm, in the order the arena's own boot screen fetches it — so a
 * hand-off cut short by the cap leaves the *later* files cold rather than a random half of
 * each set.
 *
 * The bundle warm runs alongside the assets rather than before them: it is the one fetch the
 * navigation genuinely blocks on, and serialising it behind ~5MB of GLBs would defeat the
 * point of starting it early.
 */
async function warm(target: string, progress: BootProgress, fetchImpl: typeof fetch): Promise<void> {
  const client = warmClient(target, progress, fetchImpl);
  const assets = (async () => {
    await prefetchIntoCache(weaponPrefetchUrls(), (fraction) => progress.set('weapons', fraction));
    await prefetchIntoCache(characterModelPrefetchUrls(), (fraction) =>
      progress.set('characters', fraction),
    );
  })();
  await Promise.all([client, assets]);
}

/**
 * Warms the arena document and the module URLs it names.
 *
 * Every failure here is ignorable by design — a cold bundle is the behaviour this module
 * replaced, not a broken one — so the stage is marked complete either way and the bar keeps
 * moving.
 */
async function warmClient(
  target: string,
  progress: BootProgress,
  fetchImpl: typeof fetch,
): Promise<void> {
  try {
    const response = await fetchImpl(documentUrl(target));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = await response.text();
    progress.set('client', 0.5);
    const urls = moduleUrls(html);
    await Promise.all(
      urls.map(async (url) => {
        try {
          const module = await fetchImpl(url);
          if (module.ok) await module.arrayBuffer();
        } catch {
          // ignored — see the doc comment
        }
      }),
    );
  } catch (error) {
    console.warn('[handoff] could not warm the arena document', error);
  } finally {
    progress.complete('client');
  }
}

/** `/arena.html?match=…` → `/arena.html`. The query is the room's business, not the cache's. */
export function documentUrl(target: string): string {
  return target.split('#')[0]!.split('?')[0]!;
}

/**
 * The module URLs named in an arena document: `<script type="module" src=…>` and
 * `<link rel="modulepreload" href=…>`.
 *
 * A regex rather than `DOMParser`, on purpose — parsing the HTML would fetch and execute
 * nothing we want executed twice, and this only has to find two attribute shapes in output we
 * generate ourselves. Attribute order is not assumed (Vite emits `type` before `src`, dev
 * emits the reverse), and only same-origin paths are kept: warming a CDN's URL with a bare
 * `fetch` is as likely to be a CORS error in the console as a cache entry.
 */
export function moduleUrls(html: string): string[] {
  const found = new Set<string>();

  for (const match of html.matchAll(/<script\b([^>]*)>/gi)) {
    const attrs = match[1] ?? '';
    if (!/type\s*=\s*["']module["']/i.test(attrs)) continue;
    const src = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(attrs)?.[1];
    if (src && isSameOrigin(src)) found.add(src);
  }

  for (const match of html.matchAll(/<link\b([^>]*)>/gi)) {
    const attrs = match[1] ?? '';
    if (!/rel\s*=\s*["']modulepreload["']/i.test(attrs)) continue;
    const href = /\bhref\s*=\s*["']([^"']+)["']/i.exec(attrs)?.[1];
    if (href && isSameOrigin(href)) found.add(href);
  }

  return [...found];
}

/** Relative and root-relative only. `//cdn/…` counts as absolute. */
function isSameOrigin(url: string): boolean {
  return !/^[a-z][a-z0-9+.-]*:/i.test(url) && !url.startsWith('//');
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
